import { useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  BellRingingIcon,
  ChatsCircleIcon,
  CalendarIcon,
  UsersThreeIcon,
  HouseLineIcon,
  BooksIcon,
  PlugsIcon,
  SlidersHorizontalIcon,
  GearIcon,
  SignOutIcon,
  DotsThreeIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { fetchAttention } from '@/features/attention/api';
import { fetchAutonomyReadiness, fetchPendingActions } from '@/features/autonomy/api';
import { fetchSetupState, STEPS } from '@/features/setup/api';
import { useMe, useLogout } from '@/features/auth/hooks';
import { useStaffEvents } from '@/lib/sse';
import { CmdK } from '@/components/CmdK';
import { PushToggle } from '@/components/PushToggle';
import { cn } from '@smena/ui';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

const NAV: NavItem[] = [
  { to: '/attention', label: 'Внимание', icon: <BellRingingIcon weight="bold" /> },
  { to: '/dialogs', label: 'Диалоги', icon: <ChatsCircleIcon weight="bold" /> },
  { to: '/bookings', label: 'Брони', icon: <CalendarIcon weight="bold" /> },
  { to: '/clients', label: 'Клиенты', icon: <UsersThreeIcon weight="bold" /> },
  { to: '/properties', label: 'Объекты', icon: <HouseLineIcon weight="bold" /> },
  { to: '/kb', label: 'База знаний', icon: <BooksIcon weight="bold" /> },
  { to: '/integrations', label: 'Интеграции', icon: <PlugsIcon weight="bold" /> },
  { to: '/agent', label: 'Агент', icon: <SlidersHorizontalIcon weight="bold" /> },
  { to: '/account', label: 'Аккаунт', icon: <GearIcon weight="bold" /> },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { data: user } = useMe();
  const logoutMut = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  // SSE-соединение живёт весь жизненный цикл shell'а (пока пользователь залогинен).
  useStaffEvents();
  // Счётчик "Внимание" рядом с пунктом sidebar - обновляется через query invalidation по SSE.
  const attentionQ = useQuery({
    queryKey: ['attention'],
    queryFn: fetchAttention,
    refetchInterval: 30_000,
  });
  const attentionCount = attentionQ.data?.counts.total ?? 0;
  // Sprint 6: счётчик подтверждений - обновляется по SSE через QueryClient invalidation.
  const pendingQ = useQuery({
    queryKey: ['pending-actions', 'pending'],
    queryFn: () => fetchPendingActions('pending'),
    refetchInterval: 30_000,
  });
  const pendingCount = pendingQ.data?.items.length ?? 0;
  // Онбординг: если не завершён - показываем баннер сверху.
  const setupQ = useQuery({ queryKey: ['setup'], queryFn: fetchSetupState });
  // Раз в час: подсказка про автономию не срочная, а лишний запрос на каждый рендер панели -
  // это нагрузка на ровном месте.
  const readyQ = useQuery({
    queryKey: ['autonomy-readiness'],
    queryFn: fetchAutonomyReadiness,
    refetchInterval: 3_600_000,
    staleTime: 3_600_000,
  });
  const showSetupBanner = setupQ.data && !setupQ.data.completedAt;

  async function handleLogout() {
    await logoutMut.mutateAsync();
    navigate('/login', { replace: true });
  }

  return (
    <div className="grid h-dvh grid-cols-1 bg-bg text-text md:grid-cols-[220px_1fr]">
      {/* Рельс - тёмная плоскость на светлом приложении, как в прототипе (smenatech.ru/demo):
          контент дышит белым, навигация читается как отдельный слой и не спорит с карточками. */}
      <aside className="hidden flex-col bg-ink px-3 py-3.5 text-on-ink md:flex">
        <div className="flex h-8 items-center gap-2.5 px-0.5 text-base font-bold tracking-tight">
          <i className="size-2.5 rounded-full bg-accent shadow-[0_0_0_4px_rgba(21,94,239,.22)]" />
          <span>Smena</span>
        </div>
        <nav className="mt-3.5 flex flex-col gap-0.5" aria-label="Разделы панели">
          {NAV.map((item) => {
            // «Внимание» - единственная очередь: считаем и эскалации с черновиками, и
            // действия, ждущие кнопки. Раньше счётчик был разбит на два пункта меню.
            const badge =
              item.to === '/attention' && attentionCount + pendingCount > 0
                ? attentionCount + pendingCount
                : null;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-semibold transition-colors',
                    isActive
                      ? 'bg-accent text-white'
                      : 'text-muted-on-ink hover:bg-white/8 hover:text-white',
                  )
                }
              >
                <span className="grid size-5 place-items-center text-[18px]">{item.icon}</span>
                <span className="flex-1">{item.label}</span>
                {badge !== null ? (
                  <span className="min-w-5 rounded-full bg-destructive px-1.5 text-center text-[11px] font-bold text-white">
                    {badge}
                  </span>
                ) : null}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto border-t border-white/10 px-2 pt-3 text-xs text-muted-on-ink">
          Версия 0.2.0
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-panel px-5">
          {/* На телефоне рельса нет, и это единственный ориентир «где я». На десктопе то же
              самое уже говорят подсветка в меню и заголовок страницы - не дублируем. */}
          <div className="truncate font-semibold md:hidden">
            {NAV.find((n) => location.pathname.startsWith(n.to))?.label ?? 'Smena'}
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            <PushToggle />
            <div className="text-sm">
              <span className="text-muted">{user?.email}</span>
              {user?.role === 'owner' ? (
                <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-accent-fg">
                  owner
                </span>
              ) : null}
            </div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={logoutMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md border border-line-control px-3 py-1.5 text-xs hover:bg-panel2 disabled:opacity-60"
              aria-label="Выйти из панели"
            >
              <SignOutIcon weight="bold" className="size-3.5" />
              Выйти
            </button>
          </div>
        </header>
        {/* Приглашение, а не тревога: янтарь тут читался как «что-то сломалось», хотя
            настройка просто не закончена. Синий - фирменный и нейтральный. */}
        {showSetupBanner ? (
          <button
            type="button"
            onClick={() => navigate('/setup')}
            className="flex w-full items-center justify-between gap-4 border-b border-line bg-accent/8 px-5 py-2.5 text-left text-sm transition-colors hover:bg-accent/12"
          >
            <span className="flex items-center gap-2.5">
              <span className="font-semibold">Настройка не закончена</span>
              <span className="text-xs text-muted">
                шаг {setupQ.data?.currentStep ?? 1} из {STEPS.length} - Smena пока работает не в полную силу
              </span>
            </span>
            <span className="shrink-0 text-xs font-semibold text-accent-fg">Продолжить →</span>
          </button>
        ) : null}
        {/* Обещание «мы напомним, когда можно отпускать агента» надо выполнять там, где человек
            живёт каждый день, а не только в мастере настройки: онбординг он к этому моменту
            уже закрыл. Порог считает сервер (autonomy/readiness) - и по дням, и по числу
            реально подтверждённых ответов. */}
        {readyQ.data?.ready ? (
          <button
            type="button"
            onClick={() => navigate('/setup?step=10')}
            className="flex w-full items-center justify-between gap-4 border-b border-line bg-green/10 px-5 py-2.5 text-left text-sm transition-colors hover:bg-green/15"
          >
            <span className="flex items-center gap-2.5">
              <span className="font-semibold text-green">Пора отпускать агента к гостям</span>
              <span className="text-xs text-muted">
                вы проверяли его {readyQ.data.daysObserved} дн. и подтвердили{' '}
                {readyQ.data.confirmedCount} ответов - можно включить автоответы
              </span>
            </span>
            <span className="shrink-0 text-xs font-semibold text-green">Показать →</span>
          </button>
        ) : null}
        <main className="min-h-0 min-w-0 flex-1 overflow-auto p-4 pb-24 md:p-5 md:pb-5">{children}</main>
      </div>
      <MobileTabBar attention={attentionCount} pending={pendingCount} />
      <CmdK />
    </div>
  );
}

// Нижний таб-бар для телефона. Рельс с десятью пунктами на 375px не помещается, а прятать
// навигацию в гамбургер - значит спрятать счётчики: оператор не увидит, что его ждут.
// Четыре главных пункта всегда на виду, остальные - в «Ещё».
const MOBILE_PRIMARY = ['/attention', '/dialogs', '/bookings', '/properties'];

function MobileTabBar({ attention, pending }: { attention: number; pending: number }) {
  const [more, setMore] = useState(false);
  const location = useLocation();
  const primary = MOBILE_PRIMARY.map((to) => NAV.find((n) => n.to === to)!).filter(Boolean);
  const rest = NAV.filter((n) => !MOBILE_PRIMARY.includes(n.to));
  const badgeFor = (to: string) =>
    to === '/attention' && attention + pending > 0 ? attention + pending : null;

  return (
    <>
      {more ? (
        <div className="fixed inset-0 z-40 bg-ink/45 md:hidden" onClick={() => setMore(false)} aria-hidden />
      ) : null}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 flex items-stretch border-t border-line bg-panel pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="Разделы панели"
      >
        {primary.map((item) => {
          const badge = badgeFor(item.to);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMore(false)}
              className={({ isActive }) =>
                cn(
                  'relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold transition-colors',
                  isActive ? 'text-accent-fg' : 'text-muted',
                )
              }
            >
              <span className="grid size-6 place-items-center text-[20px]">{item.icon}</span>
              <span className="max-w-full truncate px-1">{item.label}</span>
              {badge !== null ? (
                <span className="absolute top-1 right-[calc(50%-20px)] min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-bold text-white">
                  {badge}
                </span>
              ) : null}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMore((v) => !v)}
          className={cn(
            'flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-semibold transition-colors',
            more ? 'text-accent-fg' : 'text-muted',
          )}
          aria-expanded={more}
        >
          <span className="grid size-6 place-items-center text-[20px]">
            <DotsThreeIcon weight="bold" />
          </span>
          Ещё
        </button>
      </nav>

      {more ? (
        <div className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-50 border-t border-line bg-panel p-2 shadow-pop md:hidden">
          {rest.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMore(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2.5 font-semibold transition-colors',
                  isActive ? 'bg-accent/10 text-accent-fg' : 'text-text',
                )
              }
            >
              <span className="grid size-5 place-items-center text-[18px]">{item.icon}</span>
              {item.label}

            </NavLink>
          ))}
        </div>
      ) : null}
    </>
  );
}
